import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowLeft, Plus, X, Upload, AlertTriangle, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const CreateRaffle = ({ adminData }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    raffle_type: 'classic',
    unit_price: '',
    prizes: [''],
    prize_images: [],
    logo: null,
    payment_alias: '',
    whatsapp_number: '',
    payment_holder_name: '',
    observations: '',
    total_numbers: ''
  });

  // Verificar si puede crear rifas
  const canCreateRaffles = () => {
    if (!adminData) return true; // Por compatibilidad
    if (adminData.is_owner) return true;
    if (!adminData.enabled) return false;
    if (adminData.subscription_status === 'expired') return false;
    if (adminData.subscription_status === 'none') return false;
    return true;
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handlePrizeChange = (index, value) => {
    const newPrizes = [...formData.prizes];
    newPrizes[index] = value;
    setFormData({ ...formData, prizes: newPrizes });
  };

  const addPrize = () => {
    setFormData({ ...formData, prizes: [...formData.prizes, ''] });
  };

  const removePrize = (index) => {
    const newPrizes = formData.prizes.filter((_, i) => i !== index);
    setFormData({ ...formData, prizes: newPrizes });
  };

  const handleImageUpload = async (e, type) => {
    const files = Array.from(e.target.files);
    const maxSize = 2 * 1024 * 1024; // 2MB

    for (let file of files) {
      if (file.size > maxSize) {
        toast.error('Las imágenes deben ser menores a 2MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        if (type === 'logo') {
          setFormData({ ...formData, logo: base64 });
        } else {
          setFormData({
            ...formData,
            prize_images: [...formData.prize_images, base64]
          });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const removePrizeImage = (index) => {
    const newImages = formData.prize_images.filter((_, i) => i !== index);
    setFormData({ ...formData, prize_images: newImages });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validaciones
      if (!formData.title || !formData.unit_price || !formData.payment_alias || !formData.whatsapp_number) {
        toast.error('Completá todos los campos obligatorios');
        setLoading(false);
        return;
      }

      const validPrizes = formData.prizes.filter(p => p.trim() !== '');
      if (validPrizes.length === 0) {
        toast.error('Agregá al menos un premio');
        setLoading(false);
        return;
      }

      if (formData.raffle_type === 'custom' && (!formData.total_numbers || formData.total_numbers <= 0)) {
        toast.error('Especificá la cantidad de números para la rifa personalizada');
        setLoading(false);
        return;
      }

      const payload = {
        title: formData.title,
        raffle_type: formData.raffle_type,
        unit_price: parseFloat(formData.unit_price),
        prizes: validPrizes,
        prize_images: formData.prize_images,
        logo: formData.logo,
        payment_alias: formData.payment_alias,
        whatsapp_number: formData.whatsapp_number,
        payment_holder_name: formData.payment_holder_name || undefined,
        observations: formData.observations || undefined,
        total_numbers: formData.raffle_type === 'custom' ? parseInt(formData.total_numbers) : undefined
      };

      await axios.post(`${API}/raffles`, payload);
      toast.success('Rifa creada exitosamente');
      navigate('/admin');
    } catch (error) {
      console.error('Error creating raffle:', error);
      toast.error(error.response?.data?.detail || 'Error al crear rifa');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Button
        variant="outline"
        onClick={() => navigate('/admin')}
        data-testid="back-button"
      >
        <ArrowLeft className="mr-2" size={20} />
        Volver
      </Button>

      {/* Mensaje de suscripción vencida */}
      {!canCreateRaffles() && (
        <Card className="bg-red-50 border-red-300">
          <CardContent className="py-6">
            <div className="flex items-center gap-4">
              <AlertTriangle className="text-red-600" size={40} />
              <div>
                <h3 className="text-xl font-bold text-red-800">No podés crear nuevas rifas</h3>
                <p className="text-red-600 mt-1">
                  {!adminData?.enabled 
                    ? 'Tu cuenta está deshabilitada. Contactá al administrador.'
                    : 'Tu suscripción ha vencido. Contactá al administrador para renovar y seguir creando rifas.'
                  }
                </p>
                <p className="text-sm text-red-500 mt-2">
                  Podés seguir gestionando tus rifas existentes desde la lista de rifas.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className={!canCreateRaffles() ? 'opacity-50 pointer-events-none' : ''}>
        <CardHeader>
          <CardTitle className="text-3xl" data-testid="create-raffle-title">Crear Nueva Rifa</CardTitle>
          <CardDescription>Completá la información de tu rifa</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Información Básica */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Información Básica</h3>
              
              <div>
                <Label htmlFor="title">Título de la Rifa *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Ej: Rifa por un Auto 0km"
                  required
                  data-testid="raffle-title"
                />
              </div>

              <div>
                <Label htmlFor="raffle_type">Tipo de Rifa *</Label>
                <Select
                  value={formData.raffle_type}
                  onValueChange={(value) => handleInputChange('raffle_type', value)}
                >
                  <SelectTrigger data-testid="raffle-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="grid_100">00-99 (100 numeros)</SelectItem>
                    <SelectItem value="classic">000-999 (1000 numeros)</SelectItem>
                    <SelectItem value="grid_10000">0000-9999 (10000 numeros)</SelectItem>
                    <SelectItem value="lines_20">20 Líneas x 5 (00-99)</SelectItem>
                    <SelectItem value="lines_50">50 Lineas x 2 (del 00 al 99)</SelectItem>
                    <SelectItem value="lines_500">500 Líneas x 2 (del 000 al 999)</SelectItem>
                    <SelectItem value="lines">200 Lineas x 5</SelectItem>
                    <SelectItem value="lines_125">125 Lineas x 8</SelectItem>
                    <SelectItem value="lines_250">250 filas Manu (250 filas x 4 numeros)</SelectItem>
                    <SelectItem value="lines_250_osvaldo">250 Líneas Osvaldo (250 filas x 4 numeros)</SelectItem>
                    <SelectItem value="custom">Personalizada</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.raffle_type === 'custom' && (
                <div>
                  <Label htmlFor="total_numbers">Cantidad de Números *</Label>
                  <Input
                    id="total_numbers"
                    type="number"
                    min="1"
                    value={formData.total_numbers}
                    onChange={(e) => handleInputChange('total_numbers', e.target.value)}
                    placeholder="Ej: 500"
                    required
                    data-testid="total-numbers"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="unit_price">Valor número/fila *</Label>
                <Input
                  id="unit_price"
                  type="text"
                  inputMode="decimal"
                  value={formData.unit_price}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^\d.,]/g, '').replace(',', '.');
                    handleInputChange('unit_price', v);
                  }}
                  placeholder="Ej: 150000"
                  required
                  data-testid="unit-price"
                />
              </div>

              {/* Selector de modo de pago eliminado - solo modo manual */}

              <div>
                <Label htmlFor="payment_alias">Alias de Pago *</Label>
                <Input
                  id="payment_alias"
                  value={formData.payment_alias}
                  onChange={(e) => handleInputChange('payment_alias', e.target.value)}
                  placeholder="Ej: RIFA.AUTO.2024"
                  required
                  data-testid="payment-alias"
                />
              </div>

              <div>
                <Label htmlFor="payment_holder_name">A nombre de</Label>
                <Input
                  id="payment_holder_name"
                  value={formData.payment_holder_name}
                  onChange={(e) => handleInputChange('payment_holder_name', e.target.value)}
                  placeholder="Ej: Juan Perez"
                  data-testid="payment-holder-name"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Nombre del titular de la cuenta del alias. El cliente lo vera para verificar la transferencia.
                </p>
              </div>

              <div>
                <Label htmlFor="whatsapp_number">Número de WhatsApp (con código de país) *</Label>
                <Input
                  id="whatsapp_number"
                  value={formData.whatsapp_number}
                  onChange={(e) => handleInputChange('whatsapp_number', e.target.value)}
                  placeholder="Ej: +54 9 11 1234-5678"
                  required
                  data-testid="whatsapp-number"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Este número recibirá las reservas de los clientes
                </p>
              </div>

              <div>
                <Label htmlFor="observations">Observaciones</Label>
                <textarea
                  id="observations"
                  value={formData.observations}
                  onChange={(e) => handleInputChange('observations', e.target.value)}
                  placeholder="Aclaraciones para los participantes (fecha, condiciones, etc.)"
                  rows={3}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid="observations-input"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Aparecerá en el detalle público de la rifa (opcional).
                </p>
              </div>
            </div>

            {/* Premios */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Premios</h3>
              {formData.prizes.map((prize, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={prize}
                    onChange={(e) => handlePrizeChange(index, e.target.value)}
                    placeholder={`Premio ${index + 1}`}
                    data-testid={`prize-${index}`}
                  />
                  {formData.prizes.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => removePrize(index)}
                      data-testid={`remove-prize-${index}`}
                    >
                      <X size={18} />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={addPrize}
                className="w-full"
                data-testid="add-prize"
              >
                <Plus className="mr-2" size={18} />
                Agregar Premio
              </Button>
            </div>

            {/* Imágenes */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Imágenes</h3>
              
              <div>
                <Label htmlFor="logo">Logo de la Rifa</Label>
                <div className="mt-2">
                  {formData.logo ? (
                    <div className="relative inline-block">
                      <img
                        src={formData.logo}
                        alt="Logo"
                        className="w-32 h-32 object-contain border rounded"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => setFormData({ ...formData, logo: null })}
                        className="absolute top-0 right-0"
                      >
                        <X size={14} />
                      </Button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-500 transition-colors">
                        <Upload className="mx-auto mb-2" size={32} />
                        <p className="text-sm text-gray-600">Click para subir logo</p>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'logo')}
                        className="hidden"
                        data-testid="logo-upload"
                      />
                    </label>
                  )}
                </div>
              </div>

              <div>
                <Label>Imágenes de Premios</Label>
                <div className="mt-2 space-y-4">
                  {formData.prize_images.length > 0 && (
                    <div className="grid grid-cols-4 gap-4">
                      {formData.prize_images.map((img, index) => (
                        <div key={index} className="relative">
                          <img
                            src={img}
                            alt={`Premio ${index + 1}`}
                            className="w-full h-24 object-cover border rounded"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => removePrizeImage(index)}
                            className="absolute top-0 right-0"
                          >
                            <X size={14} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <label className="cursor-pointer">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-500 transition-colors">
                      <Upload className="mx-auto mb-2" size={32} />
                      <p className="text-sm text-gray-600">Click para agregar imágenes de premios</p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => handleImageUpload(e, 'prizes')}
                      className="hidden"
                      data-testid="prize-images-upload"
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="flex gap-4 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin')}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                disabled={loading}
                data-testid="submit-create-raffle"
              >
                {loading ? 'Creando...' : 'Crear Rifa'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateRaffle;
